import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

interface WelcomeScreenProps {
  onStart: (consent: boolean) => void;
  onConsentChange?: (consent: boolean) => void;
}

// Variantes de animação para o componente WelcomeScreen
const welcomeVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeInOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.5, ease: "easeInOut" } }
};

export const WelcomeScreen = ({ onStart, onConsentChange }: WelcomeScreenProps) => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');

    if (emailParam) {
      setEmail(emailParam);
      handleStart();
    }
  }, []);

  const handleCheckboxChange = () => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    if (onConsentChange) {
      onConsentChange(newValue);
    }
  };

  const handleTermsCheckboxChange = () => {
    setTermsAccepted(!termsAccepted);
  };
  
  const handleStart = () => {
    if (!email) return;
    
    setIsButtonAnimating(true);
    setIsExiting(true);
    setIsVisible(false);
    
    setTimeout(() => {
      onStart(isChecked);
      navigate(`/login?email=${encodeURIComponent(email)}`);
    }, 800);
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 px-4 space-y-6"
          variants={welcomeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Card className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                  <img
                    src="https://app.thenewscc.com.br/square_logo_favicon.png"
                    alt="Coffee Cup"
                    className="w-5 h-5"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800 dark:text-gray-200">the news</span>
                    <span className="text-sm text-gray-500">06:06</span>
                  </div>
                  <div className="text-sm text-gray-500">para: você@email.com</div>
                </div>
              </div>

              <img
                src="https://app.thenewscc.com.br/email-header.jpg"
                alt="Header"
                className="object-cover rounded-t-lg rounded-b-none mb-4"
              />

              <div className="space-y-4">
                <div className="text-left space-y-1">
                  <h1 className="text-2xl font-bold">
                    <span className="text-yellow-400">seja</span><span className="text-gray-800 dark:text-gray-200"> bem-vindo(a)</span>
                  </h1>
                  <p className="text-gray-600 font-medium">
                     <span className="text-gray-600 font-extrabold text-xs dark:text-gray-400">PROGRAMA DE INDICAÇÃO</span>
                  </p>
                  <img src="https://app.thenewscc.com.br/gif_day_1.gif" alt="Programa de Indicação" className="object-cover rounded-lg mb-4" />
                </div>

                <div className="flex flex-col items-start gap-3">
                  <Input
                    type="email"
                    placeholder="Digite seu e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full border-gray-300 dark:border-gray-600"
                  />

                  <button
                    onClick={handleStart}
                    className={`bg-yellow-400 text-black dark:text-white px-5 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isButtonAnimating ? 'animate-button-pop' : ''}`}
                    disabled={isExiting || !termsAccepted || !email}
                  >
                    acessar
                  </button>

                  <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      className="form-checkbox text-yellow-400"
                    />
                    <span>
                      Concordo em compartilhar dados demográficos para melhor experiência futura.
                    </span>
                  </label>

                  <label className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={handleTermsCheckboxChange}
                      className="form-checkbox text-yellow-400"
                    />
                    <span>
                      Aceito os <a href="/terms-and-policy" className="text-yellow-400 hover:underline">termos de serviço</a> e <a href="/terms-and-policy" className="text-yellow-400 hover:underline">política de privacidade</a> do programa de indicação.
                    </span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );}; 
